import { type FC, useEffect, useRef, useState } from "react";

import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { OptionCard } from "@/shared/components/ui/OptionCard";
import { RadioGroup } from "@/shared/components/ui/radio-group";
import { Separator } from "@/shared/components/ui/separator";
import { useWillStepAnswers } from "@/shared/hooks/useWillStepAnswers";

import { LegalAuthorityStepLayout } from "./LegalAuthorityStepLayout";

type AttorneyAnswer = "prepared" | "different" | "none";

const ATTORNEY_OPTIONS: { value: AttorneyAnswer; label: string }[] = [
  { value: "prepared", label: "Yes, the attorney who prepared the will" },
  { value: "different", label: "Yes, a different attorney" },
  { value: "none", label: "No attorney yet" },
];

interface LegalAuthorityAttorneyQuestionStepProps {
  estateId: number;
  onBack: () => void;
  onContinue: (answer: AttorneyAnswer) => void;
}

export const LegalAuthorityAttorneyQuestionStep: FC<LegalAuthorityAttorneyQuestionStepProps> = ({
  estateId,
  onBack,
  onContinue,
}) => {
  const { savedAnswers, saveAnswers } = useWillStepAnswers(estateId);
  const [selected, setSelected] = useState<AttorneyAnswer | "">("");
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const hasRestored = useRef(false);

  useEffect(() => {
    if (!savedAnswers || hasRestored.current) return;
    hasRestored.current = true;
    if (savedAnswers.attorney) {
      setSelected(savedAnswers.attorney as AttorneyAnswer);
    }
  }, [savedAnswers]);

  const handleContinue = async () => {
    if (!selected) {
      setError("Please select an option");
      return;
    }
    setSaving(true);
    try {
      await saveAnswers({ attorney: selected });
      onContinue(selected);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <LegalAuthorityStepLayout
      title="Is an attorney already involved?"
      description="Many families don't have one at this point. You can add or change an attorney anytime."
      activeStep={2}
      onBack={onBack}
      actionButton={
        <Button variant="brand" size="md" onClick={handleContinue} disabled={saving}>
          Continue
        </Button>
      }
    >
      <Separator />

      <RadioGroup
        value={selected}
        onValueChange={(v) => {
          setSelected(v as AttorneyAnswer);
          setError(undefined);
        }}
        className="flex flex-col gap-6"
      >
        {ATTORNEY_OPTIONS.map((option) => (
          <OptionCard
            key={option.value}
            label={option.label}
            selected={selected === option.value}
            onClick={() => {
              setSelected(option.value);
              setError(undefined);
            }}
            variant="radio"
            value={option.value}
          />
        ))}
      </RadioGroup>
      <FieldError message={error} />
    </LegalAuthorityStepLayout>
  );
};
